/**
 * The wind chart's geometry, with the lines that make it readable (Kyle, 2026-10-02: "it needs chart lines
 * to be interpretable"): horizontal speed lines labelled down the left, time marks labelled along the
 * bottom in site-local time, wind solid and gust dashed. Pure, so the scale is tested, not eyeballed.
 *
 * Kyle, 2026-10-24: the speed scale starts at 0-30 kt with a line every 5, grows only when a gust needs
 * more room and never shrinks below that; the time axis always spans the last 6 hours (TREND_WINDOW_MS)
 * with a line every 30 minutes. Speeds are in the site's display unit, so the 30 kt floor is converted
 * and rounded up to the next line (35 mph, 60 km/h, 20 m/s).
 */

import { convertWind, type Observation, type WindUnit } from '@skyweather/weather-core'

export interface ChartBox {
  width: number
  height: number
  /** Room for the speed labels on the left and the time labels underneath. */
  left: number
  bottom: number
  top: number
  right: number
}

export const CHART_BOX: ChartBox = { width: 800, height: 230, left: 44, bottom: 26, top: 10, right: 12 }

export interface WindChart {
  wind: string
  gust: string
  /** Horizontal lines: y position and the speed it marks. */
  speedLines: { y: number; label: string }[]
  /** Vertical marks: x position and the local time it marks. */
  timeMarks: { x: number; label: string }[]
  count: number
  plot: { x0: number; x1: number; y0: number; y1: number }
}

/** The speed step between lines: always 5, in the site's display unit. */
export const SPEED_STEP = 5

/** The lowest top of the speed scale, in knots: the scale never shows less than 0-30 kt. */
export const MIN_TOP_KT = 30

/** The top of the speed scale for a highest gust: the 30 kt floor, or the next line at or above the gust. */
export function speedTop(highest: number, unit: WindUnit): number {
  const floor = Math.ceil(convertWind(MIN_TOP_KT, 'kt', unit) / SPEED_STEP - 1e-9) * SPEED_STEP
  return Math.max(floor, Math.ceil(highest / SPEED_STEP - 1e-9) * SPEED_STEP)
}

export function windChart(
  points: Observation[],
  from: number,
  to: number,
  unit: WindUnit,
  timeZone: string,
  box: ChartBox = CHART_BOX,
): WindChart {
  const usable = points.filter((p) => p.observedAt >= from && p.observedAt <= to && p.windKt !== null).sort((a, b) => a.observedAt - b.observedAt)
  const toUnit = (kt: number) => convertWind(kt, 'kt', unit)
  const highest = Math.max(0, ...usable.map((p) => toUnit(p.gustKt ?? p.windKt ?? 0)))
  const step = SPEED_STEP
  const topValue = speedTop(highest, unit)

  const x0 = box.left
  const x1 = box.width - box.right
  const y0 = box.top
  const y1 = box.height - box.bottom
  const x = (t: number) => x0 + ((t - from) / (to - from)) * (x1 - x0)
  const y = (v: number) => y1 - (v / topValue) * (y1 - y0)

  const line = (pick: (o: Observation) => number | null) =>
    usable
      .map((p) => [p, pick(p)] as const)
      .filter((pair): pair is readonly [Observation, number] => pair[1] !== null)
      .map(([p, v]) => `${x(p.observedAt).toFixed(1)},${y(toUnit(v)).toFixed(1)}`)
      .join(' ')

  const speedLines: WindChart['speedLines'] = []
  for (let v = 0; v <= topValue + 1e-9; v += step) speedLines.push({ y: y(v), label: String(v) })

  // A time mark every 30 minutes on the half hour, in the site's own clock.
  const fmt = new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  const timeMarks: WindChart['timeMarks'] = []
  const halfHour = 30 * 60_000
  for (let t = Math.ceil(from / halfHour) * halfHour; t <= to; t += halfHour) timeMarks.push({ x: x(t), label: fmt.format(t) })

  return { wind: line((o) => o.windKt), gust: line((o) => o.gustKt), speedLines, timeMarks, count: usable.length, plot: { x0, x1, y0, y1 } }
}
